import React, { useEffect, useMemo } from 'react';
import moment from 'moment';
import { useInView } from 'react-intersection-observer';
import ReactTable from '../../Table';
import TableSkeleton from '../../Table/TableSkeleton';
import Typography from '../../Typography';
import { useLoginActivity } from '../../../hooks/account';
import { resolveLoginAuthMethodLabel } from '../../../utils/constants/loginAuthMethods';

/**
 * ROAR-4942 — the Login Activity pane (AC8).
 *
 * Read-only by design: the Story says "those events list read-only — this story writes nothing
 * new", so this renders no control that could mutate anything. There is no search, no filter and
 * no row action; paging is driven by a sentinel rather than a button.
 *
 * Columns follow Figma node 263-490, with ONE deliberate departure (ROAR-4945): the middle column
 * is headed **"SIGNED IN WITH"**, not Figma's DEVICE.
 *
 * `device` is the wire field name, but it carries `user_login_histories.logged_in_using` — the
 * auth METHOD (`'password'`, `'google'`, `'email_code'`, …). The collection captures no user-agent
 * at all, so there is no browser or OS to show and Figma's column promises information this
 * endpoint cannot supply. The raw token is mapped through `loginAuthMethods` rather than rendered
 * directly, so a user reads "Email code" rather than `email_code`.
 */
const columns = [
  {
    Header: 'DATE & TIME',
    accessor: 'occurred_at',
    Cell: ({ value }) => (value ? moment(value).format('MM-DD-YYYY, hh:mm A') : '-'),
  },
  {
    Header: 'SIGNED IN WITH',
    accessor: 'device',
    Cell: ({ value }) => resolveLoginAuthMethodLabel(value),
  },
  { Header: 'LOCATION', accessor: 'location', className: 'text-right' },
];

const LoginActivity = () => {
  const { data, isLoading, hasNextPage, fetchNextPage } = useLoginActivity();
  const { ref, inView } = useInView();

  // Flatten every page — the table renders the whole accumulated list, not just the latest page.
  // Guards a page payload without `items`, which is what a failed or empty read looks like.
  const rows = useMemo(() => (data?.pages || []).flatMap((page) => page?.items || []), [data]);

  useEffect(() => {
    if (inView && hasNextPage) fetchNextPage();
  }, [inView, hasNextPage, fetchNextPage]);

  return (
    <div>
      <Typography variant={'h5'} className={'font-semibold'}>
        Login History
      </Typography>

      {isLoading ? (
        <TableSkeleton rowsNum={5} />
      ) : (
        <>
          {/* See UsageLedger: the empty state renders outside the table, so the outline moves to
              a wrapper while there are no rows. */}
          <div className={`mt-4 ${rows.length === 0 ? 'settings-table-frame' : ''}`}>
            <ReactTable
              data={rows}
              columns={columns}
              dependencyArray={[rows]}
              className={'table settings-table w-full'}
              headerClassName={'background rounded [&_th]:text-[10px] [&_th]:!font-bold'}
              bodyClassName={'text-headings'}
              emptyDataText={'No login activity yet'}
            />
          </div>
          {hasNextPage && (
            <div data-testid={'login-activity-sentinel'} className={'h-px'} ref={ref} />
          )}
        </>
      )}
    </div>
  );
};

export default LoginActivity;
